import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Grid, TextField } from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers';
import { AccessTime } from '@mui/icons-material';

import Form from 'components/Form';
import MuiDialog from './components/MuiDialog';
import useNotification from 'hooks/useNotification';

import { IMedication } from '../PatientList/api/type';

interface Props {
  isUpdate: boolean;
  defaultValue: Partial<IMedication>;
  onClose: () => void;
  onSave: (data: IMedication) => void;
  onSearchNo: (no: number) => boolean;
}

const MedicationDialog = (props: Props) => {
  const { isUpdate, defaultValue, onClose, onSave, onSearchNo } = props;
  const [isSearched, setIsSearched] = useState(isUpdate);
  const [prescriptionTime, setPrescriptionTime] = useState<Date | null>(
    new Date(defaultValue.prescriptionTime!)
  );
  const [medicationTime, setMedicationTime] = useState<Date | null>(
    new Date(defaultValue.medicationTime!)
  );

  const { onSuccess, onRequired } = useNotification();
  const { handleSubmit, register, getValues, setValue } = useForm({
    defaultValues: defaultValue,
  });

  const onSubmit = (data: any) => {
    if (!isUpdate && onSearchNo(Number(data.ptMedicationNo)))
      return onRequired('REQUIRED.UNIQUE.MEDICATION.NO');
    if (!isSearched) return onRequired('REQUIRED.MEDICATION.ID');

    onSuccess('저장되었습니다');
    onSave({ ...data, prescriptionTime, medicationTime });
    onClose();
  };

  const onChangeMedicationId = (e: any) => {
    const { value } = e.target;
    setValue('medicationId', value);
    setValue('medicationName', '');
    setIsSearched(false);
  };

  return (
    <MuiDialog.SaveForm
      isOpen
      maxWidth="md"
      title="투약기록지 데이터"
      onClose={onClose}
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Grid container gap={2} sx={{ my: 2 }}>
        <Form.Item required label={'투약번호'}>
          <TextField
            required
            size="small"
            type="number"
            {...register('ptMedicationNo')}
            InputProps={{ readOnly: isUpdate }}
          />
        </Form.Item>
        <Form.Item required label={'처방시간'}>
          <MobileTimePicker
            value={prescriptionTime}
            onChange={setPrescriptionTime}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                fullWidth={false}
                InputProps={{ endAdornment: <AccessTime /> }}
              />
            )}
          />
        </Form.Item>
        <Form.Item required label={'투여예정시간'}>
          <MobileTimePicker
            value={medicationTime}
            onChange={setMedicationTime}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                fullWidth={false}
                InputProps={{ endAdornment: <AccessTime /> }}
              />
            )}
          />
        </Form.Item>
        <Grid item xs={12} />

        <Form.Item required label={'성분명코드 / 일반명코드'}>
          <TextField
            size="small"
            {...register('medicationId')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'일반명'}>
          <TextField
            size="small"
            {...register('medicationName')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'투여방법'}>
          <TextField
            size="small"
            {...register('medicationMedicate')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'기본함량'}>
          <TextField
            size="small"
            {...register('medicationContent')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Form.Item label={'기본단위'}>
          <TextField
            size="small"
            {...register('medicationMeasure')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Grid item xs={12} />
        <Form.Item required label={'투여량'}>
          <TextField required size="small" {...register('medicationAmount')} />
        </Form.Item>
        <Form.Item required label={'투여횟수'}>
          <TextField required size="small" {...register('medicationFreq')} />
        </Form.Item>

        <Grid item xs={12}>
          <Form.Item required fullWidth label={'상세 투여방법'}>
            <TextField
              required
              fullWidth
              multiline
              minRows={5}
              size="small"
              {...register('medicationNote')}
            />
          </Form.Item>
        </Grid>
      </Grid>
    </MuiDialog.SaveForm>
  );
};

export default MedicationDialog;
